import React, { SetStateAction, useCallback, useEffect, useState } from 'react';
import { SortingRule } from 'react-table';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import SearchEyeLineIcon from 'remixicon-react/SearchEyeLineIcon';
import { useAppState } from '../../../store/appstate';
import searchActions from '../../../store/search/actions';
import { Order } from '../../../types/order/Order';
import Spinner from '../../atoms/Spinner/Spinner';
import CardBody from '../../atoms/Card/CardBody';
import ExpandableCard from '../../atoms/Card/ExpandableCard';
import ErrorMessage from '../../atoms/Message/Error/ErrorMessage';
import { ErrorType } from '../../../types/response/ErrorCodes';
import TableHeaderTypes from '../../../types/tableHeaderType';
import BulkOrderActions from './BulkActions';
import { SearchState } from '../../../store/search/searchSlice';
import SpinnerWrapper from '../../atoms/Spinner/SpinnerWrapper';
import {
    createEncodedSearchQueryString,
    createSearchQueryString,
    generateColumnString,
    parseQuery,
    parseSearchUrl,
    sortColumns,
} from '../../../services/helpers/queryStringHelpers';
import { browsePagination, createSearchPath } from '../../../services/helpers/pagination';
import { Account } from '../../../types/account/Account';
import {
    defaultAccountColumnHeaders,
    generateAccountColumnHeaders,
} from '../HeaderColumns/AccountColumnHeaders';
import {
    setOrderAccountSearchTab,
    UserSettingsState,
} from '../../../store/userSettings/userSettingsSlice';
import { ConnectedStore } from '../../../types/ConnectedStore';
import { SearchMetadata } from '../../../types/response/Metadata';
import { useAppDispatch } from '../../../store';
import Table from '../../atoms/Table/Table';
import { ColumnDef } from '../../atoms/Table/tableFeatures';
import { formatNumberWithoutDecimals } from '../../../services/helpers/numberFormats';
import OrderTabType from '../../../types/order/OrderTabType';
import ButtonTab from '../../atoms/Tabs/ButtonTab';
import { getOrderSearchFilters } from '../../../services/helpers/search/orderAccountSearchHelper';
import OrderSearchTabType from '../../../types/order/OrderTabType';
import EmptyState from '../../atoms/Information/EmptyState';
import Button from '../../atoms/Button/Button';

interface StyleProps {
    isSearching: boolean;
}

interface SearchResultProps {
    availableStores: ConnectedStore[];
    allColumnHeaders: ColumnDef<Order>[];
    defaultColumns: string[];
    tableHeaderType: TableHeaderTypes;
    setTableColumns: React.Dispatch<React.SetStateAction<ColumnDef<Order>[]>>;
    tableColumns: ColumnDef<Order>[];
    savedColumns: string[];
    resetTableSelects: boolean;
    setResetTableSelects: React.Dispatch<SetStateAction<boolean>>;
    tab: string;
    setTab: React.Dispatch<React.SetStateAction<string>>;
    isExpanded?: boolean;
    onExpandToggle?: () => void;
    onClearSearch?: () => void;
}

const SearchResults: React.FC<SearchResultProps> = ({
    availableStores,
    allColumnHeaders,
    defaultColumns,
    tableHeaderType,
    setTableColumns,
    tableColumns,
    savedColumns,
    resetTableSelects,
    setResetTableSelects,
    tab,
    setTab,
    isExpanded,
    onExpandToggle,
    onClearSearch,
}: SearchResultProps) => {
    const [clearCheckedRows, setClearCheckedRows] = useState(() => () => {});
    const [selectedOrders, setSelectedOrders] = useState([] as Order[]);

    const theme = useTheme();

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { orderColumnFilters: sessionColumns, exactSearch } = useAppState<UserSettingsState>(
        s => s.userSettings
    );

    const { pathname, search: queryString } = useLocation();
    const { query, filterMap, sort, perPage, columns, page } = parseSearchUrl(
        queryString,
        sessionColumns
    );

    const allAccountColumnHeaders = generateAccountColumnHeaders(availableStores);
    const accountHeadersAsList = allAccountColumnHeaders.map(x => x.header) as string[];
    const columnString = generateColumnString(columns);
    const accountColumnString = generateColumnString(accountHeadersAsList);

    const handlePerPageChange = (newPerPage: string) => {
        const currentPage = parseInt(page || '1', 10);
        const oldPerPage = parseInt(perPage || '30', 10);
        const newPerPageNum = parseInt(newPerPage, 10);
        const currentOffset = (currentPage - 1) * oldPerPage;
        const newPage = Math.max(1, Math.floor(currentOffset / newPerPageNum) + 1);

        const navigateTo = createEncodedSearchQueryString(
            parseQuery(query, exactSearch ?? false),
            getOrderSearchFilters(filterMap),
            sort,
            newPerPage,
            columns,
            String(newPage),
            tab
        );
        browsePagination(navigate, pathname, navigateTo);
        dispatch(searchActions.searchOrders(navigateTo));
    };

    const {
        isSearching,
        isCanceled,
        searchFailed,
        error,
        searchResult: { links: orderLinks, metaData: orderMetaData, data: orderData = [] },
        isSearchingAccounts,
        accountSearchCanceled,
        searchAccountsError = undefined,
        searchAccountsResult: {
            links: accountLinks,
            metaData: accountMetaData,
            data: accountData = [],
        },
    } = useAppState<SearchState>(s => s.search);

    const getNumberOfRecordsTextFromMetaData = (recordsMetaData: SearchMetadata) => {
        return recordsMetaData
            ? `${formatNumberWithoutDecimals(recordsMetaData.totalNumberOfRecords)}`
            : '0';
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sortOrderColumns = (sortby: Array<SortingRule<any>>) => {
        const params = sortColumns(sortby, orderLinks.self?.query);

        navigate(createSearchPath(params, pathname));

        dispatch(searchActions.searchOrders(params));
    };

    const getCorrectTabToDirectTo = (tabName: string) => {
        switch (tabName) {
            case OrderSearchTabType.Invoices:
                return 'accounts';
            default:
                return 'orders';
        }
    };

    const onRowClick = (row: Order | Account, newTab = false) => {
        const redirectTab = getCorrectTabToDirectTo(tab);

        if (newTab) {
            const win = window.open(`/${redirectTab}/${row.id}`, '_blank');
            if (win) win.focus();
        } else {
            navigate(`/${redirectTab}/${row.id}`, { state: { from: `${queryString}` } });
        }
    };

    const setSelectedRowsCallBack = useCallback(
        (orders: Order[]) => {
            if (selectedOrders.length !== orders.length) setSelectedOrders(orders);
        },
        [setSelectedOrders, selectedOrders]
    );

    const resetBulkState = () => {
        setSelectedOrders([]);
        clearCheckedRows();
    };

    const handleClickOnTab = (tabToSave: string) => {
        const isOrderTab = tabToSave === OrderSearchTabType.Orders;
        const navigateTo = createEncodedSearchQueryString(
            parseQuery(query, exactSearch ?? false),
            getOrderSearchFilters(filterMap),
            sort,
            perPage,
            columns,
            page,
            isOrderTab ? OrderSearchTabType.Orders : OrderSearchTabType.Invoices
        );

        dispatch(setOrderAccountSearchTab(tabToSave));
        browsePagination(navigate, pathname, navigateTo);
    };

    useEffect(() => {
        if (resetTableSelects) {
            setSelectedOrders([]);
            clearCheckedRows();
            setResetTableSelects(false);
        }
    }, [resetTableSelects]);

    const renderEmptyState = (entity: string) => (
        <EmptyState
            title={`No ${entity} found`}
            icon={<SearchEyeLineIcon size={theme.icon.size.xlarge} color={theme.colors.accent3} />}
        >
            <div>{`No ${entity} match your search and filters.`}</div>
            {onClearSearch && (
                <Button
                    type="button"
                    onClick={onClearSearch}
                    data-testid="empty-state-clear-button"
                >
                    Clear search and filters
                </Button>
            )}
        </EmptyState>
    );

    const renderOrders = () => {
        if (searchFailed)
            return (
                <ErrorMessageWrapper>
                    <ErrorMessage error={error} errorHeader={ErrorType.Search} />
                </ErrorMessageWrapper>
            );

        return (
            <>
                {(isSearching || isCanceled) && (
                    <SpinnerWrapper>
                        <Spinner text="Searching..." loading={isSearching || isCanceled} />
                    </SpinnerWrapper>
                )}

                <TableWrapper isSearching={isSearching || isCanceled}>
                    {!!orderMetaData && orderData.length === 0 ? (
                        renderEmptyState('orders')
                    ) : (
                        <Table<Order>
                            data={orderData}
                            allColumns={allColumnHeaders}
                            columns={tableColumns}
                            savedColumns={savedColumns}
                            setColumns={setTableColumns}
                            onSelectedRowsChange={setSelectedRowsCallBack}
                            defaultColumns={defaultColumns}
                            tableHeaderType={tableHeaderType}
                            sort={{
                                initialSortbyState: sort,
                                manualsorting: true,
                                sortFunction: sortOrderColumns,
                            }}
                            pagination={{
                                metaData: orderMetaData,
                                links: orderLinks,
                                pageSize: perPage,
                                nextPage: () =>
                                    browsePagination(
                                        navigate,
                                        pathname,
                                        `${orderLinks.next?.query}&tab=${OrderTabType.Orders}${columnString}`
                                    ),
                                previousPage: () =>
                                    browsePagination(
                                        navigate,
                                        pathname,
                                        `${orderLinks.prev?.query}&tab=${OrderTabType.Orders}${columnString}`
                                    ),
                                perPage: { value: perPage, onChange: handlePerPageChange },
                            }}
                            onRowClicked={onRowClick}
                            columnMenu
                            isExpanded={isExpanded}
                            onExpandToggle={onExpandToggle}
                            uncheckAllDelegate={setClearCheckedRows}
                        />
                    )}
                </TableWrapper>
            </>
        );
    };

    const renderAccounts = () => {
        const generatePaginationUrlForAccounts = (paginationLink?: string) => {
            const {
                filterMap: accountFilters,
                sort: accountSort,
                perPage: accountPerPage,
                columns: accountColumns,
                page: accountPage,
                tab: accountTab,
            } = parseSearchUrl(paginationLink ?? '');

            return createSearchQueryString(
                query,
                getOrderSearchFilters(accountFilters),
                accountSort,
                accountPerPage,
                accountColumns,
                accountPage,
                accountTab
            );
        };

        if (searchAccountsError)
            return (
                <ErrorMessageWrapper>
                    <ErrorMessage error={searchAccountsError} errorHeader={ErrorType.Search} />
                </ErrorMessageWrapper>
            );

        return (
            <>
                {(isSearchingAccounts || accountSearchCanceled) && (
                    <SpinnerWrapper>
                        <Spinner
                            text="Searching accounts..."
                            loading={isSearchingAccounts || accountSearchCanceled}
                        />
                    </SpinnerWrapper>
                )}

                <TableWrapper isSearching={isSearchingAccounts || accountSearchCanceled}>
                    {!!accountMetaData && accountData.length === 0 ? (
                        renderEmptyState('invoices')
                    ) : (
                        <Table<Account>
                            data={accountData}
                            columns={allAccountColumnHeaders}
                            defaultColumns={defaultAccountColumnHeaders}
                            tableHeaderType={tableHeaderType}
                            sort={{
                                initialSortbyState: sort,
                                manualsorting: true,
                                sortFunction: sortOrderColumns,
                            }}
                            pagination={{
                                metaData: accountMetaData,
                                links: accountLinks,
                                pageSize: perPage,
                                nextPage: () =>
                                    browsePagination(
                                        navigate,
                                        pathname,
                                        generatePaginationUrlForAccounts(
                                            `${accountLinks?.next?.query}&tab=${OrderTabType.Invoices}${accountColumnString}`
                                        )
                                    ),
                                previousPage: () =>
                                    browsePagination(
                                        navigate,
                                        pathname,
                                        generatePaginationUrlForAccounts(
                                            `${accountLinks.prev?.query}&tab=${OrderTabType.Invoices}${accountColumnString}`
                                        )
                                    ),
                            }}
                            onRowClicked={onRowClick}
                            isExpanded={isExpanded}
                            onExpandToggle={onExpandToggle}
                        />
                    )}
                </TableWrapper>
            </>
        );
    };

    return (
        <Container>
            <ButtonTabsActions expanded={isExpanded}>
                <ButtonTabs>
                    <ButtonTab
                        large
                        tabKey={OrderTabType.Orders}
                        text={'Orders'}
                        setTab={setTab}
                        currentTab={tab}
                        subLabel={getNumberOfRecordsTextFromMetaData(orderMetaData)}
                        onClick={() => handleClickOnTab(OrderTabType.Orders)}
                    />
                    <ButtonTab
                        large
                        tabKey={OrderTabType.Invoices}
                        text={'Invoices'}
                        setTab={setTab}
                        currentTab={tab}
                        subLabel={getNumberOfRecordsTextFromMetaData(accountMetaData)}
                        onClick={() => handleClickOnTab(OrderTabType.Invoices)}
                    />
                </ButtonTabs>

                {tab === OrderTabType.Orders && (
                    <ActionsRow>
                        <BulkOrderActions
                            selectedOrders={selectedOrders}
                            executed={resetBulkState}
                            availableStores={availableStores}
                            visibleColumns={columns}
                        />
                    </ActionsRow>
                )}
            </ButtonTabsActions>

            <ExpandableCard expanded={isExpanded}>
                <CardBody>
                    {tab === OrderTabType.Orders && renderOrders()}
                    {tab === OrderTabType.Invoices && renderAccounts()}
                </CardBody>
            </ExpandableCard>
        </Container>
    );
};

export default SearchResults;

const TableWrapper = styled.div<StyleProps>`
    opacity: ${props => (props.isSearching ? 0.5 : 1)};
`;

const Container = styled.div``;

const ErrorMessageWrapper = styled.div`
    margin: 2rem;
    padding-top: 1rem;
`;

const ButtonTabsActions = styled.div<{ expanded?: boolean }>`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
`;

const ButtonTabs = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: 2rem;
    width: fit-content;
    border-radius: 0.5rem;
    background: ${props => props.theme.colors.medium};
`;

const ActionsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 2rem;
`;
